import React, { useEffect, useState } from "react";
import { Button } from "../../../../_helpers/components/Button";
import { useNavigate } from "react-router-dom";
import { addBadgeage, getBadgeages } from "../../../../_api/Badgeuse";
import toast from "react-hot-toast";
import { SideBar } from "../../../../_helpers/components/SideBar";
import {
    getAskValidations,
    getAskValidators,
} from "../../../../_api/AskValidation";
import { AskForm } from "./AskForm";
import { useOutletContext } from "react-router-dom";
import { dateUTCToLocal } from "../../../../_helpers/functions";

const statusToText = {
    'ACCEPTED': 'Acceptée',
    'REFUSED': 'Refusée',
    'PENDING': 'En attente',
    'PAST': 'Passée',
    'CANCELED': 'Annulée',
}

const statusColors = {
    'ACCEPTED': {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-200',
        icon: 'M5 13l4 4L19 7'
    },
    'REFUSED': {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-200',
        icon: 'M6 18L18 6M6 6l12 12'
    },
    'PENDING': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-200',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    'PAST': {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    'CANCELED': {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        icon: 'M6 18L18 6M6 6l12 12'
    },
}

const formatDateDDMMYYYY = (dateInput) => {
    const date = new Date(dateInput);
    const pad = (num) => (num < 10 ? "0" + num : num);
    return `${pad(date.getDate())}/${pad(
        date.getMonth() + 1
    )}/${date.getFullYear()}`;
};

const formatDateTime = (dateInput) => {
    const date = new Date(dateInput);
    const pad = (num) => (num < 10 ? "0" + num : num);
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} à ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const StatusBadge = ({ status }) => {
    const statusConfig = statusColors[status] || statusColors['PENDING'];

    return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
            <svg
                className="w-4 h-4 mr-1.5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={statusConfig.icon}
                />
            </svg>
            {statusToText[status] || status}
        </span>
    );
};

const ValidationCard = ({ validation, onClick }) => {
    const statusConfig = statusColors[validation.status] || statusColors['PENDING'];

    return (
        <div
            onClick={() => onClick && onClick(validation)}
            className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 ${onClick ? 'cursor-pointer' : ''} overflow-hidden group`}
        >
            <div className={`h-1 ${statusConfig.bg.replace('bg-', 'bg-').replace('-100', '-500')}`}></div>
            <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                            <div className="flex items-center justify-center w-8 h-8 bg-linear-to-br from-app-primary to-app-secondary rounded-full text-white font-semibold text-xs">
                                {validation.user?.first_name?.[0]?.toUpperCase() || ""}
                                {validation.user?.last_name?.[0]?.toUpperCase() || ""}
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-app-primary transition-colors">
                                    {validation.user?.first_name} {validation.user?.last_name}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <StatusBadge status={validation.status} />
                </div>

                <div className="space-y-2">
                    <div className="flex items-start space-x-2">
                        <svg
                            className="w-4 h-4 text-gray-400 mt-0.5 shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        <p className="text-sm text-gray-600">
                            {validation.start_date !== validation.end_date
                                ? `Du ${formatDateDDMMYYYY(validation.start_date)} au ${formatDateDDMMYYYY(validation.end_date)}`
                                : `Le ${formatDateDDMMYYYY(validation.start_date)}`}
                        </p>
                    </div>

                    {validation.remarks && (
                        <div className="flex items-start space-x-2">
                            <svg
                                className="w-4 h-4 text-gray-400 mt-0.5 shrink-0"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                />
                            </svg>
                            <p className="text-xs text-gray-500 line-clamp-2">
                                {validation.remarks}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const BadgeageCard = ({ badgeage }) => {
    const aDate = new Date(badgeage.data.badged_at);
    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 p-4">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                        {badgeage.data.badgeuse ? (
                            <div className="flex items-center justify-center w-10 h-10 bg-linear-to-br from-cyan-500 to-cyan-800 rounded-full text-white">
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center w-10 h-10 bg-linear-to-br from-indigo-200 to-indigo-400 rounded-full text-white">
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                </svg>
                            </div>
                        )
                        }

                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">
                                {badgeage.data.badgeuse ? badgeage.data.badgeuse.name : "Pointage manuel"}
                            </h3>
                            <p className="text-xs text-gray-500">
                                {aDate.toLocaleDateString()} à {aDate.toLocaleTimeString()}
                            </p>
                        </div>
                    </div>
                    {badgeage.data.badgeuse ? badgeage.data.badgeuse.remarks && (
                        <p className="text-xs text-gray-500 ml-13">
                            {badgeage.data.badgeuse.remarks}
                        </p>
                    ) : (
                        <p className="text-xs text-gray-500 ml-13">
                            {badgeage.data.comment}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

const Home = () => {
    const navigate = useNavigate();
    const [user, setUser, currentApp, currentMenu] = useOutletContext();
    const features_right = user.policy.rights[currentApp][currentMenu.id]["sub_menu"]["MAIN"]["features"];
    const isTimeTrackingEnabled = user?.use_time_tracking === true;
    const hasLinkedWebBadgeuse = user?.badgeuses?.some((b) => b.type === "WEB");
    const [askValidations, setAskValidations] = useState([]);
    const [askValidators, setAskValidators] = useState([]);

    const [openSideBar, setOpenSidebar] = useState(false);
    const [sideBarTitle, setSidebarTitle] = useState("");
    const [sideBarContent, setSidebarContent] = useState("");
    const [badgeages, setBadgeages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isBadging, setIsBadging] = useState(false);

    useEffect(() => {
        getAskValidation();
        getAskValidator();
        if (isTimeTrackingEnabled) {
            loadBadgeages();
        } else {
            setIsLoading(false);
        }
    }, []);

    const loadBadgeages = async () => {
        setIsLoading(true);
        let startDate = new Date();
        startDate.setDate(startDate.getDate() - 15);

        const payload = {
            user_id: user.id,
            badge_number: user.badge_number,
            start_date: startDate.toISOString().slice(0, 19).replace("T", " "),
            end_date: null,
        };

        await getBadgeages(payload)
            .then((resp) => {
                const res = resp.data.map((res) => {
                    const aDate = new Date(res.badged_at);
                    return {
                        row: [
                            aDate.toDateString(),
                            aDate.toTimeString(),
                            res.badgeuse ? res.badgeuse.name : "Pointage manuel",
                            res.badgeuse ? res.badgeuse.remarks : res.comment,
                        ],
                        data: res,
                    };
                });
                setBadgeages(res);
            })
            .catch((resp) => {
                console.log(resp);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const badge = () => {
        setIsBadging(true);
        addBadgeage({})
            .then((resp) => {
                const aDate = new Date(resp.data.time);
                toast(
                    <ul className="p-0 max-w-md space-y-1 text-gray-500 list-inside">
                        <li className="flex items-center">
                            <svg
                                className="w-3.5 h-3.5 me-2 text-green-500 shrink-0"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                            </svg>
                            {resp.data.message} à {aDate.toLocaleTimeString()}
                        </li>
                    </ul>
                );
                loadBadgeages();
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || "Une erreur est survenue.");
            })
            .finally(() => {
                setIsBadging(false);
            });
    };

    const getAskValidation = () => {
        getAskValidations().then((resp) => {
            let data = resp.data.filter((res) => {
                return res.status === "PENDING";
            });
            setAskValidations(data);
        });
    };

    const getAskValidator = () => {
        getAskValidators("PENDING").then((resp) => {
            setAskValidators(resp.data);
        });
    };

    return (
        <div className="min-h-full bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                Accueil Planning
                            </h1>
                            {isTimeTrackingEnabled && (
                                <p className="mt-2 text-sm text-gray-600">
                                    Gérez vos demandes, validations et badgeages
                                </p>
                            )}
                            {!isTimeTrackingEnabled && (
                                <p className="mt-2 text-sm text-gray-600">
                                    Gérez vos demandes et validations
                                </p>
                            )}

                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                action={(e) => navigate("/planning")}
                                name={"Mon planning"}
                            />
                            {isTimeTrackingEnabled && hasLinkedWebBadgeuse && (
                                <Button
                                    type="empty"
                                    action={(e) => badge()}
                                    name={"Je badge"}
                                    loading={isBadging}
                                />
                            )}
                            <Button
                                type="empty"
                                action={() => {
                                    setSidebarTitle("Réaliser une demande");
                                    setSidebarContent(
                                        <AskForm
                                            closeModal={() => setOpenSidebar(false)}
                                            refresh={getAskValidation}
                                        />
                                    );
                                    setOpenSidebar(true);
                                }}
                                isVisible={features_right['add_leaves']}
                                name={"Je fais une demande"}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Mes demandes */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center w-10 h-10 bg-linear-to-br from-app-primary to-app-secondary rounded-lg">
                                        <svg
                                            className="w-6 h-6 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">
                                            Mes demandes
                                        </h2>
                                        <p className="text-sm text-gray-500">
                                            {askValidations.length} demande{askValidations.length > 1 ? 's' : ''} en attente
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate("/validation#PENDING")}
                                    className="text-sm text-app-primary hover:text-app-secondary font-medium"
                                >
                                    Voir tout →
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            {askValidations.length === 0 ? (
                                <div className="text-center py-8">
                                    <svg
                                        className="mx-auto h-12 w-12 text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                    <h3 className="mt-4 text-sm font-medium text-gray-900">
                                        Aucune demande en attente
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500">
                                        Vous n'avez aucune demande en cours de validation.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {askValidations.slice(0, 5).map((validation) => (
                                        <ValidationCard
                                            key={validation.id}
                                            validation={validation}
                                        />
                                    ))}
                                    {askValidations.length > 5 && (
                                        <div className="text-center pt-2">
                                            <button
                                                onClick={() => navigate("/validation#PENDING")}
                                                className="text-sm text-app-primary hover:text-app-secondary font-medium"
                                            >
                                                Voir les {askValidations.length - 5} autres →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Validations en attente */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center w-10 h-10 bg-linear-to-br from-yellow-500 to-yellow-600 rounded-lg">
                                        <svg
                                            className="w-6 h-6 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">
                                            Validations en attente
                                        </h2>
                                        <p className="text-sm text-gray-500">
                                            {askValidators.length} demande{askValidators.length > 1 ? 's' : ''} à valider
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate("/validation#PENDING")}
                                    className="text-sm text-app-primary hover:text-app-secondary font-medium"
                                >
                                    Voir tout →
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            {askValidators.length === 0 ? (
                                <div className="text-center py-8">
                                    <svg
                                        className="mx-auto h-12 w-12 text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <h3 className="mt-4 text-sm font-medium text-gray-900">
                                        Aucune validation en attente
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500">
                                        Aucune demande ne vous est adressée pour le moment.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {askValidators.slice(0, 5).map((validation) => (
                                        <ValidationCard
                                            key={validation.id}
                                            validation={validation}
                                        />
                                    ))}
                                    {askValidators.length > 5 && (
                                        <div className="text-center pt-2">
                                            <button
                                                onClick={() => navigate("/validation#PENDING")}
                                                className="text-sm text-app-primary hover:text-app-secondary font-medium"
                                            >
                                                Voir les {askValidators.length - 5} autres →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Derniers badgeages */}
                {isTimeTrackingEnabled && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center w-10 h-10 bg-linear-to-br from-blue-500 to-blue-600 rounded-lg">
                                        <svg
                                            className="w-6 h-6 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">
                                            Derniers badgeages
                                        </h2>
                                        <p className="text-sm text-gray-500">
                                            Vos 15 derniers badgeages
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            {isLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-primary"></div>
                                </div>
                            ) : badgeages.length === 0 ? (
                                <div className="text-center py-8">
                                    <svg
                                        className="mx-auto h-12 w-12 text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 0 0118 0z"
                                        />
                                    </svg>
                                    <h3 className="mt-4 text-sm font-medium text-gray-900">
                                        Aucun badgeage récent
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500">
                                        Aucun badgeage n'a été enregistré récemment.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {badgeages.map((badgeage, index) => (
                                        <BadgeageCard key={index} badgeage={badgeage} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <SideBar
                title={sideBarTitle}
                content={sideBarContent}
                isOpen={openSideBar}
                onClose={() => {
                    setOpenSidebar(false);
                }}
            />
        </div>
    );
};

export default Home;
